import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Clock, MapPin, Award, Wrench, Home, Shield, Droplet, Zap, CheckCircle, Users, AlertTriangle, Gauge, Scissors, Camera, User } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';
import { createReviewSchema } from '../lib/schemas/aiSeoSchemas';

const CentennialPlumber = () => {
  // Schema markup data for Centennial
  const localBusinessSchema = createLocalBusinessSchema('Centennial', {
    lat: '39.5807',
    lng: '-104.8756'
  });

  const waterHeaterServiceSchema = createServiceSchema(
    'Water Heater Installation - Centennial',
    'Professional water heater installation and replacement in Centennial, Colorado. Starting at $2,000 with transparent pricing.',
    2000
  );

  const sewerServiceSchema = createServiceSchema(
    'Sewer Camera Inspection - Centennial',
    'Professional sewer camera scope inspection in Centennial to diagnose issues before expensive repairs. Starting at $175.',
    175
  );

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://colorado-plumbing.com/' },
    { name: 'Service Areas', url: 'https://colorado-plumbing.com/#service-area' },
    { name: 'Centennial Plumber', url: 'https://colorado-plumbing.com/centennial-plumber' }
  ]);

  // Enhanced Organization Schema for E-E-A-T
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Colorado First Plumbing and Sewer LLC",
    "alternateName": "Colorado First Plumbing",
    "url": "https://colorado-plumbing.com",
    "logo": "https://colorado-plumbing.com/logo.png",
    "foundingDate": "2018",
    "founders": [
      {
        "@type": "Person",
        "name": "Codee Jessen",
        "jobTitle": "Master Plumber"
      },
      {
        "@type": "Person",
        "name": "Michelle Jessen",
        "jobTitle": "Owner (Majority)"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360",
      "bestRating": "5"
    }
  };

  // Comprehensive FAQPage Schema for AI Optimization - NO JSX COMPONENTS
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What's the affordable cost for sewer camera inspection in Centennial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable sewer camera inspection in Centennial for $175 complete with detailed video report and recommendations. This transparent pricing helps avoid expensive surprises by showing exactly what is wrong with your sewer line before costly repairs are needed."
        }
      },
      {
        "@type": "Question",
        "name": "How much does tankless water heater replacement cost in Centennial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides affordable tankless water heater replacement in Centennial for $5,000 (existing tankless to tankless) or $8,000 (new tankless installation). This includes professional installation, proper venting, gas line modifications, and all permits required by Arapahoe County."
        }
      },
      {
        "@type": "Question",
        "name": "What's the affordable cost for 40-gallon water heater replacement in Centennial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable 40-gallon water heater replacement in Centennial for $2,000 (summer special) or $2,425 regular price. This all-inclusive price covers the Rheem or AO Smith unit, professional installation, thermal expansion tank, drain pan, new flex lines, and disposal of your old unit."
        }
      },
      {
        "@type": "Question",
        "name": "How much does affordable 50-gallon water heater installation cost in Centennial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides affordable 50-gallon water heater installation in Centennial for $2,100 (summer special) or $2,525 regular price. Perfect for larger families in Southglenn and Cherry Knolls neighborhoods who need more hot water capacity with professional installation by our Master Plumber team."
        }
      },
      {
        "@type": "Question",
        "name": "What does affordable sewer spot repair cost in Centennial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable sewer spot repair in Centennial for $5,000 for yard repairs or $9,500 for street repairs. This fixes specific problem areas without replacing the entire line. Street repairs cost more due to permits and restoration required by the City of Centennial."
        }
      },
      {
        "@type": "Question",
        "name": "How much does affordable full sewer replacement cost in Centennial?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides affordable full sewer replacement in Centennial for $12,000 using trenchless pipe bursting technology. This comprehensive solution replaces your entire sewer line from house to city main with minimal disruption to landscaping - important for HOA compliance in Centennial communities."
        }
      }
    ]
  };

  // BirdEye widget loading
  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  useEffect(() => {
    // CRITICAL: Meta tags must match generate-static.cjs exactly
    document.title = "Centennial Plumber | Colorado First Plumbing | Real Prices";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Centennial\'s ONLY affordable plumber showing actual prices online. Sewer scope $175, water heater $2,000. 1000+ reviews across all platforms. Master plumber owned. Same-day service.');
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/centennial-plumber');
      document.head.appendChild(canonicalLink);
    } else {
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/centennial-plumber');
    }

    // Business Schema Implementation with Centennial coordinates
    const businessSchema = createLocalBusinessSchema('Centennial', {
      lat: '39.5807',
      lng: '-104.8772'
    });

    // Real Centennial Customer Reviews for AI SEO
    const centennialReviews = [
      {
        author: "Sarah M.",
        rating: 5,
        text: "Excellent service! Codee and his team replaced our water heater in Centennial quickly and professionally. They showed up on time and completed the work exactly as quoted online. No surprises!",
        date: "2024-04-12",
        platform: "Google"
      },
      {
        author: "Centennial Customer",
        rating: 5,
        text: "Finally found an honest plumber in Centennial! Colorado First Plumbing actually shows their prices online instead of sending a salesman. Fixed our garbage disposal at the exact price quoted.",
        date: "2024-03-28",
        platform: "BirdEye"
      },
      {
        author: "David L.",
        rating: 5,
        text: "Had a sewer backup emergency in Centennial. They responded same day and had it fixed within hours. Professional service and fair pricing. Highly recommend!",
        date: "2024-02-15",
        platform: "Angi"
      }
    ];

    const reviewSchema = createReviewSchema(centennialReviews);

    // Inject Business Schema
    let businessSchemaScript = document.querySelector('#centennial-business-schema');
    if (businessSchemaScript) {
      businessSchemaScript.textContent = JSON.stringify(businessSchema);
    } else {
      businessSchemaScript = document.createElement('script');
      businessSchemaScript.setAttribute('id', 'centennial-business-schema');
      businessSchemaScript.setAttribute('type', 'application/ld+json');
      businessSchemaScript.textContent = JSON.stringify(businessSchema);
      document.head.appendChild(businessSchemaScript);
    }

    // Inject Review Schema
    let reviewSchemaScript = document.querySelector('#centennial-review-schema');
    if (reviewSchemaScript) {
      reviewSchemaScript.textContent = JSON.stringify(reviewSchema);
    } else {
      reviewSchemaScript = document.createElement('script');
      reviewSchemaScript.setAttribute('id', 'centennial-review-schema');
      reviewSchemaScript.setAttribute('type', 'application/ld+json');
      reviewSchemaScript.textContent = JSON.stringify(reviewSchema);
      document.head.appendChild(reviewSchemaScript);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SchemaInjector 
        schemas={[localBusinessSchema, organizationSchema, waterHeaterServiceSchema, sewerServiceSchema, breadcrumbSchema, faqPageSchema]}
        title="Centennial Plumber | Colorado First Plumbing | Real Prices"
        description="Centennial's ONLY affordable plumber showing actual prices online. Sewer scope $175, water heater $2,000. 1000+ reviews across all platforms. Master plumber owned. Same-day service."
        canonical="https://colorado-plumbing.com/centennial-plumber"
        keywords="affordable plumber Centennial, transparent pricing Centennial, actual plumbing costs Centennial, honest plumber Centennial Colorado, no hidden fees plumber, 1000 reviews Centennial"
      />
      <Header />
      <main>
        {/* Hero Section - Affordable Messaging Integration */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10">
            {/* Trust Signals Banner */}
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <span className="text-white font-bold">🏆 Centennial's Most Affordable Family-Owned Plumbers • Licensed Master Plumber • Woman Majority Owned • 1000+ Reviews Across All Platforms</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
              Centennial's ONLY Affordable Plumber with Exact Online Prices
              <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                Colorado First Plumbing and Sewer LLC
              </span>
            </h1>
            
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              How can others claim "affordable" without showing prices? We prove affordable means transparent - 
              see every exact price online before you call. No hidden fees, no salesmen.
            </p>
            
            {/* Price Preview Cards - Clickable Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Sewer Scope: $175</span>
              </Link>
              <Link to="/flat-rate-affordable-rheem-water-heaters-in-colorado" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">40 Gal Rheem Standard: $2,000</span>
              </Link>
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Tankless Replace: $5,000</span>
              </Link>
              <Link to="/sewer-replacement-spot-repair-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Spot Repair: $5,000</span>
              </Link>
            </div>
            
            {/* BirdEye Widget */}
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
              <div id="birdeye-fallback" style={{ display: 'none' }} className="text-blue-100">
                <p>Reviews loading... See our 1000+ verified reviews across Google, BirdEye, and HomeAdvisor.</p>
              </div>
            </div>
            
            {/* Enhanced 5-Card Trust Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Centennial, Southglenn, Cherry Knolls, and surrounding Arapahoe County areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">1000+</div>
                <div className="text-lg font-semibold text-white mb-2">Reviews Online</div>
                <p className="text-blue-100 text-sm">4.8⭐ average across Google (313+), BirdEye (300+), HomeAdvisor (92+), and other online sources</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Centennial, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 1000+ Verified Reviews Across All Platforms</p>
              </div>
            </div>
            
            {/* Primary CTA - Action Color Psychology */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <a 
                href="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See Transparent Pricing
              </a>
            </div>
            
            {/* Primary CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <a 
                href="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See All Transparent Pricing
              </a>
            </div>
          </div>
        </section>
        
        {/* How Can Others Claim "Affordable" Section - NEW */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              How Can Others Claim "Affordable" Without Showing Prices?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor in Colorado proving affordable means transparent. True affordable plumbing shows <Link to="/water-heater-pricing" className="text-yellow-300 hover:text-yellow-200 underline">water heater costs</Link>, <Link to="/tankless-water-heater-pricing" className="text-yellow-300 hover:text-yellow-200 underline">tankless prices</Link>, <Link to="/sewer-repair-replacement" className="text-yellow-300 hover:text-yellow-200 underline">sewer repair rates</Link>, and more - all online before you call.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                Affordable doesn't mean hidden fees or surprise charges. It means eliminating sales commissions, showing exact prices, and delivering professional service at fair rates. No salespeople, no upsells, just honest plumbing.
              </p>
              
              {/* Price Comparison Cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">✅ Our Affordable Price: $175 (Sewer Camera Scope)</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✓ Exact price online</li>
                    <li>✓ No hidden fees</li>
                    <li>✓ Includes video report</li>
                    <li>✓ Written recommendations</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-red-400 font-bold mb-4">❌ Others' "Affordable": $???</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✗ "Free estimate"</li>
                    <li>✗ High-pressure sales</li>
                    <li>✗ Price varies by salesperson</li>
                    <li>✗ Hidden upcharges</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section - Affordable Professional Services */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white">
                Affordable Professional Plumbing Services in Centennial
              </h2>
              <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
                From <Link to="/residential-plumbing" className="text-blue-200 hover:text-blue-100 underline">emergency repairs</Link> to complete <Link to="/bathroom-kitchen-remodel" className="text-blue-200 hover:text-blue-100 underline">home remodels</Link>, we provide <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">affordable transparent pricing</Link> for every service
              </p>
            </div>
            
            {/* Service Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Water Heater Replacement */}
              <Link to="/water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Droplet className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Water Heater Replacement</h3>
                <p className="text-blue-100">Rheem and AO Smith atmospheric. 40-gallon $2,000, 50-gallon $2,100!</p>
              </Link>
              
              {/* Tankless Water Heater */}
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Zap className="w-12 h-12 text-yellow-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Tankless Water Heater</h3>
                <p className="text-blue-100">Navien, Rinnai replacement $5,000. New install $8,000.</p>
              </Link>
              
              {/* Sewer Repair & Replacement */}
              <Link to="/sewer-repair-replacement" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Wrench className="w-12 h-12 text-green-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Repair & Replacement</h3>
                <p className="text-blue-100">Spot repair $5,000, full replacement $12,000. Trenchless options.</p>
              </Link>
              
              {/* Sewer Cleaning & Scope */}
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Camera className="w-12 h-12 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Cleaning & Scope</h3>
                <p className="text-blue-100">Camera scope $175. Cleaning $295-$695. Root removal available.</p>
              </Link>
            </div>
            
            {/* Additional Services Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
              <Link to="/bathroom-kitchen-remodel" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Home className="w-12 h-12 text-orange-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Remodel & Renovation</h3>
                <p className="text-blue-100">Complete bathroom and kitchen plumbing for renovations.</p>
              </Link>
              
              <Link to="/commercial-plumbing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Shield className="w-12 h-12 text-red-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Commercial Plumbing</h3>
                <p className="text-blue-100">Professional commercial plumbing services for businesses.</p>
              </Link>
              
              <Link to="/pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <CheckCircle className="w-12 h-12 text-teal-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Much More</h3>
                <p className="text-blue-100">Faucets, garbage disposals, fixtures, and comprehensive services.</p>
              </Link>
              
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Gauge className="w-12 h-12 text-pink-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Diagnostic Services</h3>
                <p className="text-blue-100">$95 standard diagnosis, $225 difficult diagnosis, $350 annual checkup.</p>
              </Link>
            </div>

          </div>
        </section>
        
        {/* FAQ 1: Sewer Camera Inspection - ALTERNATING GRADIENT PATTERN */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                  What's the Affordable Cost for Sewer Camera Inspection in Centennial?
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-lg mb-6 text-blue-100">
                      <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable sewer camera inspection in Centennial for $175 complete</strong> with detailed video report and recommendations. This transparent pricing helps Centennial homeowners avoid expensive surprises by showing exactly what's wrong with your sewer line before costly repairs are needed.
                    </p>
                    <p className="text-blue-100 mb-4">
                      Our affordable camera inspection service is particularly valuable for homes in Southglenn and Cherry Knolls where mature trees and aging infrastructure can cause sewer line problems. We provide a comprehensive video recording and written report showing the exact location and nature of any issues found.
                    </p>
                    <p className="text-blue-100 mb-6">
                      The $175 flat-rate price includes access through your cleanout (no additional toilet removal fees), professional high-definition video inspection, and a detailed emailed report with our recommendations. This affordable diagnostic prevents guesswork and ensures you only pay for repairs you actually need.
                    </p>
                    
                    {/* Price Comparison */}
                    <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
                      <h4 className="text-green-300 font-bold mb-2">✅ Our Affordable Price: $175</h4>
                      <p className="text-green-100 text-sm">Includes video inspection, written report, and recommendations</p>
                    </div>
                    <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                      <h4 className="text-red-300 font-bold mb-2">❌ Competitors: $200-$300+</h4>
                      <p className="text-red-100 text-sm">Often with hidden fees and upsell pressure</p>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/page-specific-content/centennial-plumber/sewer-scope-double-cleanout-access.jpg" 
                      alt="Professional sewer camera inspection equipment accessing double cleanout in Centennial Colorado showing affordable $175 pricing"
                      className="rounded-lg shadow-lg max-w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ 2: Tankless Water Heater Replacement */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                  How Much Does Tankless Water Heater Replacement Cost in Centennial?
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-lg mb-6 text-blue-100">
                      <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC provides affordable tankless water heater replacement in Centennial for $5,000 (existing tankless to tankless) or $8,000 (new tankless installation).</strong> This includes professional installation, proper venting, gas line modifications up to 5 feet, and all permits required by Arapahoe County.
                    </p>
                    <p className="text-blue-100 mb-4">
                      For Centennial homes built in newer developments like Piney Creek and Heritage Greens, tankless replacement is often straightforward since the proper infrastructure is already in place. The $5,000 replacement price applies when you have an existing permitted tankless system with proper venting.
                    </p>
                                        
                    {/* Links to pricing pages */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                      <Link to="/tankless-water-heater-pricing" className="inline-flex items-center text-blue-200 hover:text-blue-100 font-semibold">
                        See All Tankless Models & Prices →
                      </Link>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/page-specific-content/centennial-plumber/Tankless-water-heater-installed.jpg" 
                      alt="Professional tankless water heater installation in Centennial Colorado showing affordable $5000 replacement pricing"
                      className="rounded-lg shadow-lg max-w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ 3: 40-Gallon Water Heater Replacement */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                  What's the Affordable Cost for 40-Gallon Water Heater Replacement in Centennial?
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-lg mb-6 text-blue-100">
                      <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable 40-gallon water heater replacement in Centennial for $2,000 (summer special) or $2,425 regular price.</strong> This all-inclusive price covers the Rheem or AO Smith unit, professional installation to current IPC codes, thermal expansion tank, drain pan, new flex lines, and disposal of your old unit.
                    </p>
                    <p className="text-blue-100 mb-4">
                      For homes in Central Centennial and County Line Road areas, our affordable installation includes proper BTU adjustments for altitude and compliance with Arapahoe County building codes. The summer special price of $2,000 represents exceptional value for a complete professional installation.
                    </p>
                    <p className="text-blue-100 mb-6">
                      Our affordable pricing includes everything needed for a code-compliant installation: new water supply lines, gas drip leg, proper venting connections, and professional disposal of your old unit. No hidden fees or surprise charges - the price quoted is the price paid.
                    </p>
                    
                    <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
                      <h4 className="text-blue-300 font-bold mb-2">🌟 Summer Special: $2,000 (Save $425!)</h4>
                      <p className="text-blue-100 text-sm">Regular price: $2,425 - Special ends September 15th</p>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/page-specific-content/centennial-plumber/thermal-expansion-tank-new-bradford-white.jpg" 
                      alt="Professional 40-gallon water heater installation with thermal expansion tank in Centennial Colorado showing affordable $2000 pricing"
                      className="rounded-lg shadow-lg max-w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 4: 50-Gallon Water Heater Installation */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                  How Much Does Affordable 50-Gallon Water Heater Installation Cost in Centennial?
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-lg mb-6 text-blue-100">
                      <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC provides affordable 50-gallon water heater installation in Centennial for $2,100 (summer special) or $2,525 regular price.</strong> Perfect for larger families in Southglenn and Cherry Knolls neighborhoods who need more hot water capacity with professional installation by our Master Plumber team.
                    </p>
                    <p className="text-blue-100 mb-4">
                      For Centennial homes with 3-4 people and multiple bathrooms, the 50-gallon size provides the ideal balance of capacity and affordability. Our installation meets all current plumbing codes and includes proper venting adjustments for our altitude.
                    </p>
                    <p className="text-blue-100 mb-6">
                      The affordable installation includes professional Bradford White or Rheem units specifically rated for Colorado's climate and water conditions. We handle all permitting with Arapahoe County and coordinate required inspections at no additional charge.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                      <Link to="/flat-rate-affordable-rheem-water-heaters-in-colorado" className="inline-flex items-center text-blue-200 hover:text-blue-100 font-semibold">
                        See Current Water Heater Specials →
                      </Link>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/page-specific-content/centennial-plumber/proper-venting-atmospheric-shiny-new-piping-above-water-heater.jpg" 
                      alt="Professional 50-gallon water heater installation with proper venting in Centennial Colorado showing affordable $2100 pricing"
                      className="rounded-lg shadow-lg max-w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ 5: Sewer Spot Repair */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                  What Does Affordable Sewer Spot Repair Cost in Centennial?
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-lg mb-6 text-blue-100">
                      <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable sewer spot repair in Centennial for $5,000 for yard repairs or $9,500 for street repairs.</strong> This fixes specific problem areas without replacing the entire line. Street repairs cost more due to permits and restoration required by the City of Centennial.
                    </p>
                    <p className="text-blue-100 mb-4">
                      For homes in older Centennial neighborhoods like Cherry Knolls and Southglenn, spot repair is often the most affordable solution when camera inspection shows localized damage. Our experienced crew can typically complete yard repairs in 1-2 days with minimal disruption to your landscaping.
                    </p>
                    <p className="text-blue-100 mb-6">
                      The affordable spot repair includes excavation, pipe replacement with high-quality PVC, proper bedding and backfill, and final camera inspection to verify successful completion. We coordinate all permits and restoration work at no additional charge.
                    </p>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/page-specific-content/centennial-plumber/cast-iron-removed-and-corroded-from-basement-under-slab.jpg" 
                      alt="Professional sewer spot repair showing corroded cast iron pipe removal in Centennial Colorado with affordable $5000 pricing"
                      className="rounded-lg shadow-lg max-w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Master Plumber Attribution Box */}
        <section className="py-12 bg-gradient-to-bl from-blue-900 to-blue-950">
          <div className="container-custom max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <img 
                    src="/page-specific-content/centennial-plumber/Colorado-first-plumbing-truck.jpg" 
                    alt="Codee Jessen, Licensed Master Plumber, with Colorado First Plumbing service truck in Centennial Colorado"
                    className="w-32 h-32 rounded-full object-contain bg-white/5 border-4 border-white/20 shadow-lg"
                  />
                </div>
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-2">Written by <Link to="/team/codee-jessen" className="text-blue-200 hover:text-blue-100 underline">Codee Jessen</Link>, Licensed Master Plumber</h3>
                  <div className="text-blue-100 mb-4">
                    <p className="mb-2">• Colorado Master Plumber License</p>
                    <p className="mb-2">• 18+ Years Experience</p>
                    <p className="mb-2">• 18,000+ Jobs Completed</p>
                    <p className="mb-2">• Specialties: Water Heaters, Sewer Repair, Tankless Systems</p>
                    <p>• Proudly Serving Centennial Since 2018</p>
                  </div>
                  <p className="text-sm text-blue-200 mb-4 italic">
                    "Affordable doesn't mean cutting corners - it means efficient, professional service with transparent pricing. Proud to serve the Centennial community with honest, upfront plumbing solutions."
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link to="/team/michelle-jessen" className="inline-flex items-center text-blue-200 hover:text-blue-100 font-semibold">
                      <Users className="w-4 h-4 mr-2" />
                      Woman Majority Owned Business
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 6: Full Sewer Replacement */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                  How Much Does Affordable Full Sewer Replacement Cost in Centennial?
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-lg mb-6 text-blue-100">
                      <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC provides affordable full sewer replacement in Centennial for $12,000 using trenchless pipe bursting technology.</strong> This comprehensive solution replaces your entire sewer line from house to city main with minimal disruption to landscaping - important for HOA compliance in Centennial communities.
                    </p>
                    <p className="text-blue-100 mb-4">
                      For homes in Centennial neighborhoods like Piney Creek and Heritage Greens with mature landscaping, trenchless replacement preserves your investment in yards and driveways. This affordable method requires only small access points rather than full excavation of your property.
                    </p>
                    <p className="text-blue-100 mb-6">
                      The affordable $12,000 price includes new seamless HDPE pipe rated for 50+ years, professional pipe bursting installation, all permits and inspections, and coordination with HOA requirements. Most installations are completed in 1-2 days with your landscaping virtually untouched.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                      <Link to="/sewer-repair-replacement" className="inline-flex items-center text-blue-200 hover:text-blue-100 font-semibold">
                        Learn More About Sewer Replacement →
                      </Link>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <img 
                      src="/page-specific-content/centennial-plumber/chipping-concrete-new-pvc-installed.jpg" 
                      alt="Professional full sewer replacement with new PVC installation in Centennial Colorado showing affordable $12000 trenchless pricing"
                      className="rounded-lg shadow-lg max-w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What Makes Us Different Section - COMPETITIVE PSYCHOLOGY */}
        <section className="py-16 bg-neutral-light">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-authority-navy">
                What Makes Colorado First Plumbing Different in Centennial?
              </h2>
              <p className="text-xl text-neutral-gray">
                Other companies offer "$100 off coupons" - but what good is $100 off if you don't know what the price is?
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
              {/* Competitor Problems - Warning Psychology */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-red-800 flex items-center">
                  <span className="text-3xl mr-3">❌</span> What Other Centennial Plumbers Do:
                </h3>
                <div className="space-y-4">
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-1">"We need to send someone out to take a look"</p>
                    <p className="text-red-600 text-sm">Translation: We're sending a salesman to pressure you</p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-1">"$100 OFF Today Only!"</p>
                    <p className="text-red-600 text-sm">$100 off what? They won't tell you the actual price</p>
                  </div>
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-1">Hidden pricing until arrival</p>
                    <p className="text-red-600 text-sm">You're trapped once they're at your door</p>
                  </div>
                </div>
              </div>

              {/* Our Advantages - Success Psychology */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-success-primary flex items-center">
                  <span className="text-3xl mr-3">✅</span> What We Do Different:
                </h3>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-success-primary to-success-eco p-4 rounded-lg text-white border-l-4 border-success-eco shadow-lg">
                    <p className="font-semibold text-white mb-1">Actual prices online right now</p>
                    <p className="text-success-light text-sm">See water heater, toilet, garbage disposal costs before we arrive</p>
                  </div>
                  <div className="bg-gradient-to-r from-success-primary to-success-eco p-4 rounded-lg text-white border-l-4 border-success-eco shadow-lg">
                    <p className="font-semibold text-white mb-1">No salesmen - just skilled plumbers</p>
                    <p className="text-success-light text-sm">Our techs fix problems, they don't sell you things you don't need</p>
                  </div>
                  <div className="bg-gradient-to-r from-success-primary to-success-eco p-4 rounded-lg text-white border-l-4 border-success-eco shadow-lg">
                    <p className="font-semibold text-white mb-1">6+ years serving Centennial</p>
                    <p className="text-success-light text-sm">310+ verified Google reviews + hundreds more on BirdEye, Home Advisor, Angie's List</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Final CTA - Action Psychology */}
            <div className="bg-gradient-to-r from-action-primary to-action-urgent rounded-2xl p-8 text-white text-center shadow-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Why Call a Plumbing Company to Receive a Salesman?
              </h3>
              <p className="text-lg mb-6 text-action-warm/90">
                Book with us and know exactly what the price is. No hassle pricing you can see right now.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="/pricing" 
                  className="bg-neutral-white hover:bg-neutral-light hover:scale-[1.02] text-action-urgent px-8 py-4 rounded-full text-xl font-bold inline-block transition-all duration-300 w-full sm:w-auto shadow-xl"
                >
                  💰 See Actual Prices Now →
                </a>
                <a 
                  href="https://www.google.com/search?q=Colorado+First+Plumbing+and+Sewer+LLC&tbm=lcl" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/20 hover:bg-white/30 hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 border-2 border-white w-full sm:w-auto"
                >
                  ⭐ Read 310+ Reviews →
                </a>
              </div>
              <p className="text-action-warm/80 mt-4 text-sm font-medium">
                No other plumbing contractor in Denver Metro with this many verified reviews is willing to show you their pricing online
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials Section - TRUST PSYCHOLOGY with Blue Gradient */}
        <section className="relative py-16 bg-gradient-to-br from-trust-deep to-authority-navy">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom relative z-10">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-white">
                Centennial Customers Love Our Service
              </h2>
              <p className="text-lg text-trust-light">
                See why Centennial residents trust Colorado First Plumbing for their plumbing and sewer needs
              </p>
              <div className="flex items-center justify-center mt-6 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                <div className="flex space-x-1 text-action-warm text-2xl">
                  {[...Array(5)].map((_, i) => (
                    <Award key={i} size={28} className="fill-current" />
                  ))}
                </div>
                <span className="ml-4 text-3xl font-bold text-neutral-white">4.9★</span>
                <span className="ml-3 text-trust-light font-medium">(310+ Reviews)</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Testimonial 1 - Water Heater Service - TRUST BLUE THEME */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-trust-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-trust-deep">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-trust-primary">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-trust-light px-2 py-1 rounded">Google</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "Excellent service! Codee and his team replaced our water heater in Centennial quickly and professionally. They showed up on time and completed the work exactly as quoted online. No surprises!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    S
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-authority-navy">Sarah M.</p>
                    <p className="text-sm text-neutral-gray">Centennial, CO</p>
                  </div>
                </div>
              </div>

              {/* Testimonial 2 - Honest Pricing - ACTION ORANGE THEME */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-action-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-action-urgent">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-action-primary">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-action-primary/10 px-2 py-1 rounded text-action-primary">BirdEye</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "Finally found an honest plumber in Centennial! Colorado First Plumbing actually shows their prices online instead of sending a salesman. Fixed our garbage disposal at the exact price quoted."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    C
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-authority-navy">Centennial Customer</p>
                    <p className="text-sm text-neutral-gray">Centennial, CO</p>
                  </div>
                </div>
              </div>

              {/* Testimonial 3 - Emergency Response - SUCCESS GREEN THEME */}
              <div className="bg-neutral-white rounded-xl p-6 border-l-4 border-success-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-success-eco">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-success-primary">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-neutral-gray font-medium bg-success-light px-2 py-1 rounded text-success-primary">Angi</span>
                </div>
                <p className="text-neutral-gray mb-4 italic leading-relaxed">
                  "Had a sewer backup emergency in Centennial. They responded same day and had it fixed within hours. Professional service and fair pricing. Highly recommend!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    D
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-authority-navy">David L.</p>
                    <p className="text-sm text-neutral-gray">Centennial, CO</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Local Projects Showcase - AUTHORITY BUILDING */}
            <div className="mt-12 bg-gradient-to-r from-trust-light to-success-light rounded-2xl p-8 border border-trust-primary/20">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4 text-authority-navy">Recent Centennial Projects</h3>
                <p className="text-neutral-gray">
                  Hundreds of successful plumbing and sewer projects completed in Centennial neighborhoods
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div className="bg-neutral-white rounded-lg p-6 shadow-lg border border-neutral-border hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Droplet size={32} className="text-white" />
                  </div>
                  <h4 className="font-bold mb-2 text-authority-navy">Complete Sewer Replacement</h4>
                  <p className="text-sm text-neutral-gray">Southglenn neighborhood • Trenchless method • 2-day completion</p>
                </div>
                
                <div className="bg-neutral-white rounded-lg p-6 shadow-lg border border-neutral-border hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-action-urgent to-red-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Clock size={32} className="text-white" />
                  </div>
                  <h4 className="font-bold mb-2 text-authority-navy">Emergency Water Line Repair</h4>
                  <p className="text-sm text-neutral-gray">Cherry Knolls area • Sunday evening • Fixed in 3 hours</p>
                </div>
                
                <div className="bg-neutral-white rounded-lg p-6 shadow-lg border border-neutral-border hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Wrench size={32} className="text-white" />
                  </div>
                  <h4 className="font-bold mb-2 text-authority-navy">Master Bathroom Remodel</h4>
                  <p className="text-sm text-neutral-gray">Piney Creek • Complete plumbing upgrade • Luxury fixtures</p>
                </div>
              </div>
            </div>

            {/* Social Proof CTA - Updated for Blue Background */}
            <div className="mt-12 text-center">
              <p className="text-lg text-trust-light mb-6">
                Join <span className="font-bold text-action-warm">Hundreds of satisfied Centennial customers</span> who trust Colorado First Plumbing
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <a 
                  href="https://www.google.com/search?q=Colorado+First+Plumbing+and+Sewer+LLC&tbm=lcl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-action-warm hover:text-neutral-white font-semibold inline-flex items-center space-x-2 hover:underline transition-colors border border-action-warm/30 px-6 py-3 rounded-full hover:bg-white/10"
                >
                  <span>⭐ View All 310+ Reviews</span>
                  <span>→</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Contact & Service Area Section - FINAL CONVERSION PSYCHOLOGY */}
        <section className="py-16 bg-gradient-to-br from-neutral-light to-trust-light/30 text-gray-800">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-authority-navy">
                Ready to Serve Your Centennial Home
              </h2>
              <p className="text-xl text-neutral-gray">
                Local plumbing experts available during business hours for emergency service throughout Centennial and surrounding areas
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Information - CONVERSION OPTIMIZED */}
              <div className="space-y-8">
                <div className="text-center lg:text-left">
                  <h3 className="text-2xl font-bold mb-6 text-authority-navy">Get Immediate Service</h3>
                  
                  {/* Phone CTA - ACTION PSYCHOLOGY */}
                  <div className="bg-neutral-white rounded-2xl p-6 shadow-xl border-2 border-trust-primary/20 mb-6 hover:shadow-2xl transition-all duration-300">
                    <div className="flex items-center justify-center lg:justify-start mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mr-4 shadow-lg">
                        <Phone size={24} className="text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-neutral-gray font-medium">Call Now for Same-Day Service</p>
                        
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-success-primary rounded-full"></div>
                        <span className="text-authority-dark font-medium">Emergency Service Available (8 AM - 5 PM)</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-success-primary rounded-full"></div>
                        <span className="text-authority-dark font-medium">Licensed & Insured</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-success-primary rounded-full"></div>
                        <span className="text-authority-dark font-medium">Free Estimates</span>
                      </div>
                    </div>
                  </div>

                  {/* Emergency Service Info - URGENT PSYCHOLOGY */}
                  <div className="bg-gradient-to-r from-action-urgent to-red-700 rounded-xl p-6 text-white shadow-xl border-l-4 border-red-600">
                    <div className="flex items-center justify-center lg:justify-start mb-3">
                      <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mr-3">
                        <Clock size={20} className="text-white" />
                      </div>
                      <h4 className="font-bold text-lg">🚨 Emergency Service Available</h4>
                    </div>
                    <p className="text-red-100 text-center lg:text-left">
                      Burst pipes, sewer backups, no hot water? Emergency service available during business hours (8 AM - 5 PM Mon-Sat) with same-day response when possible. Priority scheduling for urgent repairs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Service Area Details - TRUST BUILDING */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-center lg:text-left text-authority-navy">Centennial Service Areas</h3>
                
                {/* Neighborhoods Grid - AUTHORITY COLORS */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-neutral-white rounded-lg p-4 shadow-lg border-l-4 border-trust-primary hover:shadow-xl transition-all duration-300">
                    <div className="w-6 h-6 bg-gradient-to-r from-action-primary to-action-warm rounded-full flex items-center justify-center mb-2">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-semibold mb-1 text-authority-navy">Central Centennial</h4>
                    <ul className="text-sm text-neutral-gray space-y-1">
                      <li>• Centennial Center</li>
                      <li>• County Line Road</li>
                      <li>• Arapahoe Road</li>
                    </ul>
                  </div>
                  
                  <div className="bg-neutral-white rounded-lg p-4 shadow-lg border-l-4 border-success-primary hover:shadow-xl transition-all duration-300">
                    <div className="w-6 h-6 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mb-2">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-semibold mb-1 text-authority-navy">Southglenn</h4>
                    <ul className="text-sm text-neutral-gray space-y-1">
                      <li>• Southglenn</li>
                      <li>• Streets of Southglenn</li>
                      <li>• Dry Creek</li>
                    </ul>
                  </div>
                  
                  <div className="bg-neutral-white rounded-lg p-4 shadow-lg border-l-4 border-trust-deep hover:shadow-xl transition-all duration-300">
                    <div className="w-6 h-6 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mb-2">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-semibold mb-1 text-authority-navy">Cherry Knolls</h4>
                    <ul className="text-sm text-neutral-gray space-y-1">
                      <li>• Cherry Knolls</li>
                      <li>• Willow Creek</li>
                      <li>• Cottonwood</li>
                    </ul>
                  </div>
                  
                  <div className="bg-neutral-white rounded-lg p-4 shadow-lg border-l-4 border-action-warm hover:shadow-xl transition-all duration-300">
                    <div className="w-6 h-6 bg-gradient-to-r from-action-warm to-action-primary rounded-full flex items-center justify-center mb-2">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-semibold mb-1 text-authority-navy">Piney Creek</h4>
                    <ul className="text-sm text-neutral-gray space-y-1">
                      <li>• Piney Creek</li>
                      <li>• Heritage Greens</li>
                      <li>• Fox Hollow</li>
                    </ul>
                  </div>
                </div>

                {/* Service Radius - SUCCESS PSYCHOLOGY */}
                <div className="bg-gradient-to-r from-success-light to-success-primary/10 rounded-xl p-4 border-2 border-success-primary/20">
                  <div className="flex items-center justify-center lg:justify-start mb-2">
                    <div className="w-6 h-6 bg-success-primary rounded-full flex items-center justify-center mr-3">
                      <MapPin size={16} className="text-white" />
                    </div>
                    <h4 className="font-bold text-success-primary">✅ Complete Centennial Coverage</h4>
                  </div>
                  <p className="text-success-primary/80 text-center lg:text-left text-sm font-medium">
                    Serving all Centennial zip codes: 80121, 80122, 80111, 80112. Plus nearby Englewood, Littleton, and Greenwood Village.
                  </p>
                </div>
              </div>
            </div>

            {/* Final CTA Section - ULTIMATE CONVERSION */}
            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 shadow-2xl border border-trust-primary/20">
                <h3 className="text-2xl font-bold mb-4 text-neutral-white">Why Choose Colorado First Plumbing for Your Centennial Home?</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center mb-3 shadow-lg">
                      <Phone size={24} className="text-white" />
                    </div>
                    <h4 className="font-bold text-neutral-white mb-2">💰 Upfront Pricing Online</h4>
                    <p className="text-sm text-trust-light">Only contractor with hundreds of verified reviews showing actual prices</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center mb-3 shadow-lg">
                      <Clock size={24} className="text-white" />
                    </div>
                    <h4 className="font-bold text-neutral-white mb-2">⚡ Same-Day Service</h4>
                    <p className="text-sm text-trust-light">Local dispatch, faster response</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center mb-3 shadow-lg">
                      <Award size={24} className="text-white" />
                    </div>
                    <h4 className="font-bold text-neutral-white mb-2">🚫 No "Send Someone Out"</h4>
                    <p className="text-sm text-trust-light">Know exactly what you'll pay before we arrive</p>
                  </div>
                </div>

                <div className="space-y-4 md:space-y-0 md:space-x-4 md:flex md:justify-center">
                  
                  <a 
                    href="/pricing" 
                    className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300 w-full md:w-auto"
                  >
                    💰 View Pricing
                  </a>
                </div>
                
                <p className="text-trust-light mt-4 text-sm">
                  Serving Centennial since 2018 • Licensed Master Plumber • BBB A+ Rating • 4.9★ Reviews
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CentennialPlumber;